import colorLib from '@kurkle/color';
import arrayFixerObjects from './arrayFixerObjects';

const COLORS = [
  '#4dc9f6',
  '#f67019',
  '#f53794',
  '#537bc4',
  '#acc236',
  '#166a8f',
  '#00a950',
  '#58595b',
  '#8549ba',
];

export function color(index) {
  return COLORS[index % COLORS.length];
}

export function transparentize(value, opacity) {
  const alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return colorLib(value).alpha(alpha).rgbString();
}

export const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  purple: 'rgb(153, 102, 255)',
  blueDark: 'rgb(12, 73, 255)',
  blackLigth: 'rgb(74, 168, 252)',
  greenDark: 'rgb(43, 130, 58)',
  oxfordBlue: 'rgb(0,33,71)',
  pastelBlue: 'rgb(174,198,207)',
  springGreen: 'rgb(0,250,154)',
  darkPastel: 'rgb(94,59,34)',
  drab: 'rgb(150,113,23)',
  darkcerulean: 'rgb(8,69,126)',
  skyBlue: 'rgb(135,206,250)',
  ultramarine: 'rgb(63,0,255)',
  akt: 'rgb(54, 162, 235)',
  auteco: 'rgb(12, 73, 255)',
  hero: 'rgb(162,214,147)',
  honda: 'rgb(255, 159, 64)',
  mobility: 'rgb(27, 27, 27)',
  otras: 'rgb(153, 102, 255)',
  yamaha: 'rgb(201, 203, 207)',
  suzuki: 'rgb(255, 205, 86)',
  uma: 'rgb(255, 99, 132)',
};

export function valueOrDefault(value, defaultValue) {
  return typeof value === 'undefined' ? defaultValue : value;
}

export const optionsSimpleBarChart = {
  type: 'bar',
  plugins: {
    datalabels: {
      display: false,
    },
  },
  grid: {
    drawOnChartArea: false,
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      stacked: true,
      display: true,
      position: 'left',
    },
    y: {
      grid: {
        display: false,
      },
      stacked: true,
      display: true,
      position: 'right',
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  maintainAspectRatio: true,
  responsive: true,
};

export const optionsGroupedCharts = {
  responsive: true,
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    datalabels: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      stacked: true,
    },
    y: {
      grid: {
        display: false,
      },
      display: false,
      stacked: true,
    },
  },
};

export const optionsLine = {
  responsive: true,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    datalabels: {
      display: false,
    },
  },
  stacked: false,
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      grid: {
        drawOnChartArea: false, // only want the grid lines for one axis to show up
      },
    },
  },
};

export const simpleBarLine = (
  array = [],
  paramFilter1,
  paramFilter2,
  paramFilter3,
  label,
  multi = 100,
) => ({
  labels: array.map((date) => date[paramFilter1]),
  datasets: [
    {
      label,
      data: array.map((value) => value[paramFilter2]),
      borderColor: [
        transparentize(CHART_COLORS.suzuki, 0.4),
        transparentize(CHART_COLORS.mobility, 0.4),
      ],
      backgroundColor: [
        transparentize(CHART_COLORS.suzuki, 0.4),
        transparentize(CHART_COLORS.mobility, 0.4),
      ],
      borderWidth: 2,
      yAxisID: 'y',
    },
    {
      label: 'Variación',
      data: array.map((variacion) => variacion[paramFilter3] * multi),
      yAxisID: 'y1',
      type: 'line',
      backgroundColor: transparentize(CHART_COLORS.purple, 0.5),
    },
  ],
});

export const barLineDataGroup = (array = [], paramFilter = '') => {
  const data = {};
  array.forEach((element) => {
    Object.keys(element).forEach((item) => {
      if (item !== 'NMES') {
        let value = element[item];
        data[item] = data[item] || [];
        if (item !== paramFilter) {
          value *= 100;
        }
        data[item].push(value);
      }
    });
  });

  // Labels eje x
  const labels = array.reduce((r, a) => {
    const search = r.findIndex((empresa) => empresa === a[paramFilter]);
    if (search === -1) {
      r.push(a[paramFilter]);
    }
    return r;
  }, []);

  const dataSet = Object.keys(data)
    .filter((value) => value !== paramFilter)
    .map((value, index) => ({
      label: value,
      data: data[value],
      borderColor: Object.values(CHART_COLORS)[index],
      backgroundColor: transparentize(
        Object.values(CHART_COLORS)[index],
        0.1,
      ),
      borderWidth: 2,
      stack: 'Stack 0',
    }));

  return {
    labels,
    datasets: dataSet,
  };
};

export const customBarLineDataGroup = (
  array = [],
  paramFilter = '',
  paramFilter2,
  date,
  multi = 100,
) => {
  const totalArray = arrayFixerObjects(array, date, paramFilter);
  // Labels eje x
  const labels = array.reduce((r, a) => {
    const search = r.findIndex((empresa) => empresa === a[date]);
    if (search === -1) {
      r.push(a[date]);
    }
    return r;
  }, []);

  // ordeno por key, paramFilter
  const graph = totalArray.reduce((r, a) => {
    r[a[paramFilter]] = [...(r[a[paramFilter]] || []), a[paramFilter2] * multi];
    return r;
  }, {});

  const dataSet = Object.keys(graph).map((value, index) => {
    const colorIndex = Object.keys(CHART_COLORS).findIndex(
      (itemColor) => itemColor === value.toLowerCase(),
    );
    return {
      label: value.toUpperCase(),
      data: graph[value],
      borderColor:
        Object.values(CHART_COLORS)[colorIndex]
        || Object.values(CHART_COLORS)[index],
      backgroundColor: transparentize(
        Object.values(CHART_COLORS)[colorIndex]
          || Object.values(CHART_COLORS)[index],
        0.1,
      ),
      borderWidth: 2,
      yAxisID: 'y',
    };
  });
  return {
    labels,
    datasets: dataSet,
  };
};

export const COLORS_FINDER = [
  'rgb(255, 99, 132)',
  'rgb(255, 159, 64)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(12, 73, 255)',
  'rgb(74, 168, 252)',
  'rgb(43, 130, 58)',
  'rgb(0,33,71)',
  'rgb(174,198,207)',
  'rgb(0,250,154)',
  'rgb(94,59,34)',
  'rgb(150,113,23)',
  'rgb(8,69,126)',
  'rgb(135,206,250)',
  'rgb(63,0,255)',
  'rgb(54, 162, 235)',
  'rgb(12, 73, 255)',
  'rgb(162,214,147)',
  'rgb(255, 159, 64)',
  'rgb(27, 27, 27)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
  'rgb(255, 205, 86)',
  'rgb(255, 99, 132)',
];
